import { useState, useEffect, useRef } from "react";
import {
  Plus,
  Search,
  Package2,
  Filter,
  Star,
  AlertTriangle,
  Trash2,
  BarChart3,
  CheckCircle,
  XCircle,
  Upload,
  Download,
  Settings,
  X,
  Grid,
  List,
  Printer,
} from "lucide-react";
import ProductCard from "../../components/Product/card";
import { Product } from "../../types/Product";
import BaseApi from "../../services/base-api";
import CreateEditProduct from "../../components/Product/createEdit";
import { Category } from "../../types/Category";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import Price from "../../components/Currency/Price";
import LoadingComponent from "../../components/LoadingComponent";
import { useDebounce } from "../../hooks/useDebounce";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { Tooltip } from "@mui/material";
import Pagination from "../../components/Pagination";
import { ImageWithFallback } from "../../constants/image-placeholder";
import { useCurrency } from "../../components/Currency/CurrencyContext";
import PrintSlipModal from "../../components/Product/PrintSlipModal";
import { printItemSlip } from "../../utils/itemSlip";
interface ProductStats {
  totalValue: number;
  total: number;
  active: number;
  inactive: number;
  outOfStock: number;
  lowStock: number;
  totalProducts?: number;
  activeProducts?: number;
  inactiveProducts?: number;
  lowStockProducts?: number;
  featuredProducts?: number;
  averagePrice?: number;
  outOfStockProducts?: number;
  categoryBreakdown: { category: string; count: number }[];
}

interface RelatedProduct {
  id: string;
  name: string;
  price: number;
  image: string;
  isFavorite: boolean;
}

interface BulkUploadProgress {
  status: string;
  percentage?: number;
  processed?: number;
  total?: number;
  message?: string;
  error?: string;
}

interface BulkUploadItem {
  row?: number;
  sku?: string;
  action?: string;
  productId?: string;
  error?: string;
  reason?: string;
  message?: string;
}

interface BulkUploadResultData {
  success?: boolean;
  message?: string;
  results?: {
    success?: BulkUploadItem[];
    errors?: BulkUploadItem[];
    skipped?: BulkUploadItem[];
    total?: number;
    processed?: number;
    currentRow?: number;
  };
}

const ProductsManagementPage = () => {
  const { success, err } = useToast();
  const { t } = useTranslation();
  const { permissions } = useAuth();
  const { currency } = useCurrency();
  // Check if user has permission to create or edit products
  const canCreateProduct = hasPermission(
    permissions,
    PERMISSIONS.CREATE_PRODUCTS,
  );
  const canEditProduct = hasPermission(permissions, PERMISSIONS.EDIT_PRODUCTS);
  const canDelete = hasPermission(permissions, PERMISSIONS.DELETE_PRODUCTS);
  const canView = hasPermission(permissions, PERMISSIONS.VIEW_PRODUCTS);
  // User can add products if they have either create or edit permission
  const canAddProduct = canCreateProduct || canEditProduct;

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filtered, setFiltered] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim());
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const latestRequestRef = useRef(0);
  const [view, setView] = useState<"grid" | "list">("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [apiTotalItems, setApiTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState<"name" | "price" | "stock">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [slipProduct, setSlipProduct] = useState<Product | null>(null);

  // Modals
  const [modal, setModal] = useState(false);
  const [statsModal, setStatsModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [bulkUpdateModal, setBulkUpdateModal] = useState(false);
  const [favoriteCheckModal, setFavoriteCheckModal] = useState(false);

  // Current data
  const [currentProduct, setCurrentProduct] = useState<Product | null>(null);
  const [productStats, setProductStats] = useState<ProductStats | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

  // Filters
  const [filterType, setFilterType] = useState<"all" | "featured" | "lowStock">(
    "all",
  );
  const [statusFilter, setStatusFilter] = useState("all");

  const [bulkUpdateData, setBulkUpdateData] = useState({
    isActive: true,
    isFeatured: false,
    categoryId: "",
    price: "",
  });

  const [favoriteCheckProducts, setFavoriteCheckProducts] = useState<string[]>(
    [],
  );
  const [favoriteResults, setFavoriteResults] = useState<{
    [key: string]: boolean;
  }>({});

  const [saving, setSaving] = useState(false);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState<File | null>(null);
  const [uploadingBulkProducts, setUploadingBulkProducts] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] =
    useState<BulkUploadProgress | null>(null);
  const [bulkUploadResult, setBulkUploadResult] =
    useState<BulkUploadResultData | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const defaultSortBy: "name" | "price" | "stock" = "name";
  const defaultSortOrder: "asc" | "desc" = "asc";

  const normalizeStringArray = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return Array.from(
        new Set(
          value
            .map((item) => String(item).trim())
            .filter((item) => item.length > 0),
        ),
      );
    }

    if (typeof value === "string") {
      return Array.from(
        new Set(
          value
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item.length > 0),
        ),
      );
    }

    return [];
  };

  const sanitizeSkuPart = (value: string): string =>
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const generateVariationsFromForm = (productData: any) => {
    const colors = normalizeStringArray(productData.colors);
    const sizes = normalizeStringArray(productData.sizes);
    const styles = normalizeStringArray(productData.style);

    const variationTypes: string[] = [];
    if (colors.length > 0) variationTypes.push("colour");
    if (sizes.length > 0) variationTypes.push("size");
    if (styles.length > 0) variationTypes.push("style");

    const axes: Array<{ key: "color" | "size" | "style"; values: string[] }> =
      [];
    if (colors.length > 0) axes.push({ key: "color", values: colors });
    if (sizes.length > 0) axes.push({ key: "size", values: sizes });
    if (styles.length > 0) axes.push({ key: "style", values: styles });

    const combinations = axes.reduce<Record<string, string>[]>((acc, axis) => {
      if (acc.length === 0) {
        return axis.values.map((value) => ({ [axis.key]: value }));
      }
      return acc.flatMap((existing) =>
        axis.values.map((value) => ({ ...existing, [axis.key]: value })),
      );
    }, []);

    const baseSku = String(productData.sku || "").trim();
    const variations = combinations.map((attributes, index) => {
      const suffix = Object.values(attributes)
        .map(sanitizeSkuPart)
        .filter(Boolean)
        .join("-");

      return {
        attributes,
        sku: suffix ? `${baseSku}-${suffix}` : `${baseSku}-v${index + 1}`,
        price: Number(productData.price) || 0,
        priceAdjustment: 0,
        stock: Number(productData.stock) || 0,
        images: Array.isArray(productData.images) ? productData.images : [],
        isActive: Boolean(productData.isActive),
      };
    });

    return {
      colors,
      sizes,
      styles,
      variationTypes,
      variations,
    };
  };

  useEffect(() => {
    loadData();
  }, [
    category,
    debouncedSearch,
    filterType,
    statusFilter,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
  ]);

  const buildApiUrl = (path: string) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    if (!baseUrl) {
      return `/api${normalizedPath}`;
    }
    return `${baseUrl}${normalizedPath}`;
  };

  const getAuthToken = () =>
    localStorage.getItem("authToken") || localStorage.getItem("token") || "";

  const extractFilename = (contentDisposition: string | null) => {
    if (!contentDisposition) {
      return "products-template.xlsx";
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const simpleMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    return simpleMatch?.[1] || "products-template.xlsx";
  };

  const normalizeBulkUploadResult = (
    payload: any,
  ): BulkUploadResultData | null => {
    if (!payload || typeof payload !== "object") {
      return null;
    }

    if ("results" in payload) {
      return payload as BulkUploadResultData;
    }

    if (
      "result" in payload &&
      payload.result &&
      typeof payload.result === "object" &&
      "results" in payload.result
    ) {
      return payload.result as BulkUploadResultData;
    }

    return null;
  };

  const loadData = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });

      // Backend Search
      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }

      // Include Description
      params.append("includeDescription", "true");

      // Category Filter
      if (category !== "all") {
        params.append("categoryId", category);
      }

      // Status Filter
      if (statusFilter !== "all") {
        params.append("isActive", statusFilter === "active" ? "true" : "false");
      }

      // Sorting
      if (!(sortBy === defaultSortBy && sortOrder === defaultSortOrder)) {
        params.append("sortBy", `${sortBy}_${sortOrder}`);
      }

      // Handle different filter types
      let endpoint = "/products";
      if (filterType === "featured") {
        endpoint = "/products/featured";
      } else if (filterType === "lowStock") {
        endpoint = "/products/low-stock";
      }

      const [productsRes, categoriesRes] = await Promise.all([
        BaseApi.get(`${endpoint}?${params.toString()}`),
        BaseApi.get(`/categories?page=1&limit=100`), // Fetch all categories
      ]);

      // Ignore responses from requests superseded by a newer one
      if (requestId !== latestRequestRef.current) return;

      setProducts(productsRes.data);
      setFiltered(productsRes.data);
      setApiTotalItems(
        productsRes.pagination?.totalItems || productsRes.data?.length,
      );
      setCategories(categoriesRes.data);
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error(error);
      err("Failed to load products");
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  };

  const fetchProductStats = async () => {
    try {
      const res = await BaseApi.get("/products/stats/overview");
      if (res.success) {
        // Enrich category breakdown with category names
        const enrichedStats = { ...res.data };
        if (enrichedStats.categoryBreakdown && categories?.length > 0) {
          enrichedStats.categoryBreakdown = enrichedStats.categoryBreakdown.map(
            (item: any) => {
              const cat = categories.find((c) => c.id === item.category);
              return {
                ...item,
                categoryName: cat ? cat.name.en : item.category,
              };
            },
          );
        }
        setProductStats(enrichedStats);
        setStatsModal(true);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
      err("Failed to fetch product statistics");
    }
  };

  const checkFavoriteStatus = async () => {
    if (favoriteCheckProducts?.length === 0) {
      err("Please enter product IDs to check");
      return;
    }

    try {
      setSaving(true);
      // Fetch favorites from API
      const res = await BaseApi.get(`/favorites?page=1&limit=100`);

      if (res?.data) {
        const favorites = res.data.favorites || res.data;
        const favoriteIds = new Set(
          favorites.map((fav: any) => fav.productId || fav.id),
        );

        // Check which products are favorites
        const results: { [key: string]: boolean } = {};
        favoriteCheckProducts.forEach((id) => {
          results[id] = favoriteIds.has(id);
        });

        setFavoriteResults(results);
      }
    } catch (error) {
      console.error("Error checking favorite status:", error);
      err("Failed to check favorite status");
    } finally {
      setSaving(false);
    }
  };

  const printSlip = async (product: Product) => {
    // Products with variations: ask which size/style the slip is for
    if (product.hasVariations && product.variationsCount > 0) {
      setSlipProduct(product);
      return;
    }

    try {
      const url = await printItemSlip({
        name: product.name?.en || product.name?.ar,
        model: product.sku,
        size: product.sizes?.[0] || "",
        barcode: product.id,
      });
      setPdfUrl(url);
    } catch (error: any) {
      console.error("Error generating slip:", error);
      err(error?.message || t("Failed to generate slip"));
    }
  };

  const handleDownloadSample = async () => {
    try {
      setDownloadingTemplate(true);
      const token = getAuthToken();
      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        buildApiUrl("/bulk-upload/products/template"),
        {
          method: "GET",
          headers,
        },
      );

      if (!response.ok) {
        throw new Error(`Template download failed (${response.status})`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const fileName = extractFilename(
        response.headers.get("content-disposition"),
      );

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      success("Sample template downloaded");
    } catch (error) {
      console.error("Download sample error:", error);
      err("Failed to download sample template");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleUploadWithSSE = async () => {
    if (!bulkUploadFile) {
      err("Please select a file first");
      return;
    }

    setUploadingBulkProducts(true);
    setBulkUploadProgress({ status: "uploading", percentage: 0 });
    setBulkUploadResult(null);

    const formData = new FormData();
    formData.append("file", bulkUploadFile);
    formData.append("updateExisting", "false");

    try {
      const token = getAuthToken();
      const headers: HeadersInit = {
        Accept: "text/event-stream",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        buildApiUrl("/bulk-upload/products/with-progress"),
        {
          method: "POST",
          headers,
          body: formData,
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText || `Bulk upload failed (${response.status.toString()})`,
        );
      }

      if (!response.body) {
        throw new Error("Streaming response is not available");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (!line.startsWith("data:")) {
            continue;
          }

          const payload = line.replace(/^data:\s*/, "");
          if (!payload) {
            continue;
          }

          try {
            const data = JSON.parse(payload) as BulkUploadProgress & {
              result?: any;
            };

            if (data.status === "done") {
              const normalizedResult = normalizeBulkUploadResult(
                data.result ?? data,
              );
              setBulkUploadResult(normalizedResult);
              setBulkUploadProgress({
                status: "completed",
                percentage: 100,
                processed:
                  typeof normalizedResult?.results?.processed === "number"
                    ? normalizedResult.results.processed
                    : undefined,
                total:
                  typeof normalizedResult?.results?.total === "number"
                    ? normalizedResult.results.total
                    : undefined,
                message:
                  normalizedResult?.message ||
                  data.message ||
                  "Upload completed successfully",
              });
              success("Products uploaded successfully");
              setBulkUploadFile(null);
              loadData();
            } else if (data.status === "error") {
              const uploadError = data.error || data.message || "Upload failed";
              setBulkUploadProgress({
                status: "error",
                percentage:
                  typeof data.percentage === "number"
                    ? data.percentage
                    : undefined,
                error: uploadError,
              });
              err(uploadError);
            } else {
              setBulkUploadProgress({
                status: data.status || "processing",
                percentage:
                  typeof data.percentage === "number"
                    ? data.percentage
                    : undefined,
                processed:
                  typeof data.processed === "number"
                    ? data.processed
                    : undefined,
                total: typeof data.total === "number" ? data.total : undefined,
                message: data.message,
              });
            }
          } catch (parseError) {
            console.error("SSE parse error:", parseError);
          }
        }
      }

      const pendingLine = buffer.trim();
      if (pendingLine.startsWith("data:")) {
        try {
          const pendingPayload = pendingLine.replace(/^data:\s*/, "");
          const pendingData = JSON.parse(
            pendingPayload,
          ) as BulkUploadProgress & {
            result?: any;
          };
          if (pendingData.status === "done") {
            const normalizedResult = normalizeBulkUploadResult(
              pendingData.result ?? pendingData,
            );
            setBulkUploadResult(normalizedResult);
            setBulkUploadProgress({
              status: "completed",
              percentage: 100,
              processed:
                typeof normalizedResult?.results?.processed === "number"
                  ? normalizedResult.results.processed
                  : undefined,
              total:
                typeof normalizedResult?.results?.total === "number"
                  ? normalizedResult.results.total
                  : undefined,
              message:
                normalizedResult?.message ||
                pendingData.message ||
                "Upload completed successfully",
            });
            success("Products uploaded successfully");
            setBulkUploadFile(null);
            loadData();
          }
        } catch (parseError) {
          console.error("SSE pending chunk parse error:", parseError);
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to upload file";
      setBulkUploadProgress({ status: "error", error: errorMessage });
      err(errorMessage);
    } finally {
      setUploadingBulkProducts(false);
    }
  };

  const handleSaveProduct = async (productData: any) => {
    try {
      setSaving(true);
      const { colors, sizes, styles, variationTypes, variations } =
        generateVariationsFromForm(productData);

      const createPayload = {
        ...productData,
        colors,
        sizes,
        style: styles.join(", "),
        hasVariations: variations.length > 0,
        variationTypes,
        variations,
      };

      const res = await BaseApi.post("/products", createPayload);
      if (res.success && res.data) {
        setProducts([...products, res.data]);
        loadData();
        success(t("Product created successfully!"));
        setModal(false);
      } else {
        err(res.message);
      }
    } catch (error) {
      console.error("Error saving product:", error);
      err("Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!currentProduct) return;

    try {
      setSaving(true);
      const res = await BaseApi.delete(`/products/${currentProduct.id}`);
      if (res.success) {
        setProducts(products.filter((p) => p.id !== currentProduct.id));
        success("Product deleted successfully!");
        setDeleteModal(false);
        setCurrentProduct(null);
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      err("Failed to delete product");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (product: Product) => {
    try {
      const res = await BaseApi.patch(
        `/products/${product.id}/toggle-active`,
        {},
      );
      if (res.success) {
        setProducts(
          products.map((p) =>
            p.id === product.id ? { ...p, isActive: !p.isActive } : p,
          ),
        );
        success(
          `Product ${!product.isActive ? "activated" : "deactivated"} successfully!`,
        );
        loadData();
      }
    } catch (error) {
      console.error("Error toggling product status:", error);
      err("Failed to toggle product status");
    }
  };

  const handleToggleFeatured = async (product: Product) => {
    try {
      const res = await BaseApi.patch(
        `/products/${product.id}/toggle-featured`,
        {},
      );
      if (res.success) {
        setProducts(
          products.map((p) =>
            p.id === product.id ? { ...p, isFeatured: !p.isFeatured } : p,
          ),
        );
        success(
          `Product ${!product.isFeatured ? "featured" : "unfeatured"} successfully!`,
        );
        loadData();
      }
    } catch (error) {
      console.error("Error toggling featured status:", error);
      err("Failed to toggle featured status");
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedProducts?.length === 0) {
      err("Please select products to update");
      return;
    }

    try {
      setSaving(true);
      const updates: any = {
        productIds: selectedProducts,
      };

      if (bulkUpdateData.categoryId) {
        updates.categoryId = bulkUpdateData.categoryId;
      }
      if (bulkUpdateData.price) {
        updates.price = parseFloat(bulkUpdateData.price);
      }
      updates.isActive = bulkUpdateData.isActive;
      updates.isFeatured = bulkUpdateData.isFeatured;

      const res = await BaseApi.patch("/products/bulk-update", updates);
      if (res.success) {
        success(`${selectedProducts?.length} products updated successfully!`);
        setBulkUpdateModal(false);
        setSelectedProducts([]);
        loadData();
      }
    } catch (error) {
      console.error("Error bulk updating products:", error);
      err("Failed to bulk update products");
    } finally {
      setSaving(false);
    }
  };

  // Helper Functions
  const handleAddProduct = () => {
    setModal(true);
  };

  const openDeleteModal = (product: Product) => {
    setCurrentProduct(product);
    setDeleteModal(true);
  };

  const handleCloseModal = () => {
    setModal(false);
  };

  // ========== PAGINATION CALCULATION ==========

  const finalTotalItems = apiTotalItems;
  const finalTotalPages = Math.ceil(finalTotalItems / itemsPerPage);

  // Ensure current page doesn't exceed total pages
  useEffect(() => {
    if (currentPage > finalTotalPages && finalTotalPages > 0) {
      setCurrentPage(finalTotalPages);
    }
  }, [currentPage, finalTotalPages, itemsPerPage]);

  if (initialLoading) {
    return <LoadingComponent title={t("Products")} />;
  }

  return (
    <div className="min-h-screen bg-[#f2e6db] py-8 px-4">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <div className="mx-auto">
        {/* Header */}
        <div
          className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6"
          style={{ animation: "fadeIn 0.5s ease-out" }}
        >
          <div className="bg-white px-4 sm:px-8 py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="relative bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                  <Package2 className="w-8 h-8 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-black">
                    {t("Products")}
                  </h1>
                  <p className="text-gray-700 text-sm mt-1">
                    {t("Manage your product inventory")} •{" "}
                    {finalTotalItems || filtered?.length} {t("products")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Tooltip
                  title={
                    canView
                      ? t("Download sample template")
                      : t("You don't have permission to view products")
                  }
                  placement="top"
                >
                  <button
                    disabled={!canView || downloadingTemplate}
                    onClick={handleDownloadSample}
                    className="px-3 sm:px-4 border border-gray-400 py-2 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    {downloadingTemplate ? (
                      <div
                        className="w-4 h-4 border-2 border-black border-t-transparent rounded-full"
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                    ) : (
                      <Download size={16} />
                    )}
                    <span className="hidden sm:inline">
                      {t("Download Sample")}
                    </span>
                  </button>
                </Tooltip>
                <Tooltip
                  title={
                    canAddProduct
                      ? t("Bulk upload products")
                      : t("You don't have permission to add product")
                  }
                  placement="top"
                >
                  <button
                    disabled={!canAddProduct}
                    onClick={() => {
                      setBulkUploadFile(null);
                      setBulkUploadProgress(null);
                      setBulkUploadResult(null);
                      setBulkUploadModal(true);
                    }}
                    className="px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-800 transition-colors font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    <Upload size={16} />
                    <span className="hidden sm:inline">{t("Bulk Upload")}</span>
                  </button>
                </Tooltip>
                <button
                  onClick={fetchProductStats}
                  className="px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-800 transition-colors font-semibold text-sm flex items-center gap-2"
                >
                  <BarChart3 size={16} />
                  <span className="hidden sm:inline">{t("Statistics")}</span>
                </button>
                <Tooltip
                  title={
                    canAddProduct
                      ? t("Add Product")
                      : t("You don't have permission to add product")
                  }
                  placement="top"
                >
                  <button
                    disabled={!canAddProduct}
                    onClick={handleAddProduct}
                    className="px-3 sm:px-4 border border-gray-400 py-2 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors font-semibold text-sm flex items-center gap-2"
                  >
                    <Plus size={16} />
                    {t("Add Product")}
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 sm:p-6 border-b border-gray-200 bg-gray-50 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm"
                  placeholder={t("Search products...")}
                />
              </div>

              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none appearance-none text-sm"
                >
                  <option value="all">{t("All Categories")}</option>
                  {(categories || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name.en}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <Settings className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split("-");
                    setSortBy(field as "name" | "price" | "stock");
                    setSortOrder(order as "asc" | "desc");
                  }}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none appearance-none text-sm"
                >
                  <option value="name-asc">{t("Name (A-Z)")}</option>
                  <option value="name-desc">{t("Name (Z-A)")}</option>
                  <option value="price-asc">{t("Price (Low-High)")}</option>
                  <option value="price-desc">{t("Price (High-Low)")}</option>
                  <option value="stock-asc">{t("Stock (Low-High)")}</option>
                  <option value="stock-desc">{t("Stock (High-Low)")}</option>
                </select>
              </div>

              <div className="relative">
                <Settings className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none appearance-none text-sm"
                >
                  <option value="all">{t("All Status")}</option>
                  <option value="active">{t("Active")}</option>
                  <option value="inactive">{t("Inactive")}</option>
                </select>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setView("grid")}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-1 ${
                    view === "grid"
                      ? "bg-black text-white"
                      : "bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <Grid size={16} />
                  {t("Grid")}
                </button>
                <button
                  onClick={() => setView("list")}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-1 ${
                    view === "list"
                      ? "bg-black text-white"
                      : "bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <List size={16} />
                  {t("List")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid/List */}
        {filtered?.length === 0 ? (
          <div
            className={
              "bg-white rounded-2xl shadow-xl p-16 text-center" +
              (loading
                ? " opacity-50 pointer-events-none transition-opacity"
                : "")
            }
            style={{ animation: "fadeIn 0.5s ease-out" }}
          >
            <Package2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {debouncedSearch || category !== "all" || filterType !== "all"
                ? t("No products found")
                : t("No products yet")}
            </p>
            {!debouncedSearch &&
              category === "all" &&
              filterType === "all" && (
                <button
                  onClick={handleAddProduct}
                  className="mt-4 text-black hover:underline font-semibold"
                >
                  {t("Create your first product")}
                </button>
              )}
          </div>
        ) : (
          <div
            className={
              loading ? "opacity-50 pointer-events-none transition-opacity" : ""
            }
            style={{ animation: "fadeIn 0.5s ease-out 0.1s backwards" }}
          >
            {view === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {filtered.map((product, i) => (
                  <div
                    key={product.id}
                    style={{
                      animation: `fadeIn 0.3s ease-out ${i * 0.05}s backwards`,
                    }}
                    className="relative group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
                  >
                    {/* Status Badges */}
                    <div className="absolute top-3 right-3 z-10 flex flex-col gap-1">
                      {product.isFeatured && (
                        <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Star size={16} fill="green" />
                          Featured
                        </span>
                      )}
                      {!product.isActive && (
                        <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                          Inactive
                        </span>
                      )}
                    </div>

                    <ProductCard
                      key={product.id}
                      product={product}
                      canAddToCart={false}
                      canAddToFavorite={false}
                      canEdit={canEditProduct}
                      canRate={!canEditProduct}
                      showRating={!!product.ratingCount ? true : false}
                      isShadow={false}
                    />

                    {/* Action Buttons */}
                    <div className="p-3 border-t grid grid-cols-5 gap-2">
                      <Tooltip
                        title={
                          canEditProduct
                            ? t("Feature Product")
                            : t("You don't have permission to edit products")
                        }
                        placement="top"
                      >
                        <button
                          disabled={!canEditProduct}
                          onClick={() => handleToggleFeatured(product)}
                          className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 ${
                            product.isFeatured
                              ? "bg-green-100 text-green-400 hover:bg-green-200 border border-green-400"
                              : "bg-gray-100 text-gray-500 hover:bg-gray-600 border border-gray-400"
                          }`}
                        >
                          <Star
                            size={16}
                            fill={product.isFeatured ? "green" : "white"}
                          />
                          {/* {product.isFeatured ? "Unfeatured" : "Feature"} */}
                        </button>
                      </Tooltip>
                      <Tooltip
                        title={
                          canEditProduct
                            ? t("Activate/Deactivate Product")
                            : t("You don't have permission to edit")
                        }
                        placement="top"
                      >
                        <button
                          disabled={!canEditProduct}
                          onClick={() => handleToggleActive(product)}
                          className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 ${
                            product.isActive
                              ? "bg-green-100 text-green-800 hover:bg-green-200 border border-green-400"
                              : "bg-red-100 text-red-800 hover:bg-red-200 border border-red-400"
                          }`}
                        >
                          {product.isActive ? (
                            <>
                              <CheckCircle size={16} />
                              {/* Active */}
                            </>
                          ) : (
                            <>
                              <XCircle size={16} />
                              {/* Inactive */}
                            </>
                          )}
                        </button>
                      </Tooltip>
                      <Tooltip title={t("Print Slip")} placement="top">
                        <button
                          onClick={() => printSlip(product)}
                          className="px-3 py-2 border border-purple-600 bg-white text-purple-600 rounded-lg hover:bg-purple-700 hover:text-white text-xs font-semibold flex items-center justify-center gap-1"
                        >
                          <Printer size={16} />
                          {/* Related */}
                        </button>
                      </Tooltip>
                      <Tooltip
                        title={
                          canDelete
                            ? t("Delete Product")
                            : t("You don't have permission to delete products")
                        }
                        placement="top"
                      >
                        <button
                          disabled={!canDelete}
                          onClick={() => openDeleteModal(product)}
                          className="px-3 py-2 border border-red-600 bg-white text-red-600 rounded-lg hover:bg-red-700 hover:text-white text-xs font-semibold flex items-center justify-center gap-1"
                        >
                          <Trash2 size={16} />
                          {/* Delete */}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {filtered.map((product, i) => (
                  <div
                    key={product.id}
                    style={{
                      animation: `fadeIn 0.3s ease-out ${i * 0.05}s backwards`,
                    }}
                    className="bg-white rounded-xl shadow-lg p-4 hover:shadow-xl transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <ImageWithFallback
                        src={product.images?.[0] || "/placeholder.png"}
                        alt={product.name.en}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 text-lg">
                          {product.name.en}
                        </h3>
                        <p className="text-sm text-gray-600">{product.sku}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-gray-600">
                            Stock: {product.stock}
                          </span>
                          <span className="pl-6 text-lg font-bold text-gray-900">
                            <Price amount={Number(product.price)} />
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {product.isFeatured && (
                          <Star
                            size={20}
                            className="text-yellow-500"
                            fill="currentColor"
                          />
                        )}
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            product.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {product.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Tooltip
                          title={
                            canDelete
                              ? t("Delete Product")
                              : t("You don't have permission to delete")
                          }
                          placement="top"
                        >
                          <button
                            disabled={!canDelete}
                            onClick={() => openDeleteModal(product)}
                            className="p-2 border border-red-600 text-red-600 bg-white rounded-lg hover:bg-red-700 hover:text-white"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Product Form Modal */}
      <CreateEditProduct
        isOpen={modal}
        onClose={handleCloseModal}
        onSave={handleSaveProduct}
        product={null}
        categoriesProps={categories}
      />

      {/* Delete Product Modal */}
      {deleteModal && currentProduct && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setDeleteModal(false);
            setCurrentProduct(null);
          }}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-red-600 to-red-800 px-6 py-4 rounded-t-2xl flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-white" />
              <h3 className="text-xl font-bold text-white">
                {t("Delete Product")}
              </h3>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                {t("Are you sure you want to delete")}{" "}
                <strong>{currentProduct.name.en}</strong>?
              </p>
              <p className="text-sm text-gray-600">
                {t("This is a soft delete. The product can be restored later.")}
              </p>
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => {
                  setDeleteModal(false);
                  setCurrentProduct(null);
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold"
              >
                {t("Cancel")}
              </button>
              <Tooltip
                title={
                  canDelete
                    ? t("Delete Product")
                    : t("You don't have permission to delete")
                }
                placement="top"
              >
                <button
                  onClick={handleDeleteProduct}
                  disabled={!canDelete && saving}
                  className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                      <span>{t("Deleting...")}</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-5 h-5" />
                      {t("Delete Product")}
                    </>
                  )}
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {bulkUpdateModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setBulkUpdateModal(false)}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Upload className="w-6 h-6" />
                {t("Bulk Update Products")}
              </h3>
              <button
                onClick={() => setBulkUpdateModal(false)}
                className="text-white hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-[#0d223f] border border-[#0d223f] rounded-lg p-4">
                <p className="text-sm font-semibold text-blue-900">
                  {t("Updating")} {selectedProducts?.length}{" "}
                  {t("selected product(s)")}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Category")} ({t("optional")})
                </label>
                <select
                  value={bulkUpdateData.categoryId}
                  onChange={(e) =>
                    setBulkUpdateData({
                      ...bulkUpdateData,
                      categoryId: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                >
                  <option value="">{t("Keep current category")}</option>
                  {(categories || []).map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name.en}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Price")} ({t("optional")})
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={bulkUpdateData.price}
                  onChange={(e) =>
                    setBulkUpdateData({
                      ...bulkUpdateData,
                      price: e.target.value,
                    })
                  }
                  placeholder={t("Keep current price")}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t("Status")}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() =>
                      setBulkUpdateData({ ...bulkUpdateData, isActive: true })
                    }
                    className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                      bulkUpdateData.isActive
                        ? "bg-green-50 border-green-500 text-green-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <CheckCircle className="inline mr-2" size={16} />
                    {t("Active")}
                  </button>
                  <button
                    onClick={() =>
                      setBulkUpdateData({ ...bulkUpdateData, isActive: false })
                    }
                    className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                      !bulkUpdateData.isActive
                        ? "bg-red-50 border-red-500 text-red-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <XCircle className="inline mr-2" size={16} />
                    {t("Inactive")}
                  </button>
                </div>
              </div>

              <div>
                <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                  <span className="text-sm font-semibold text-gray-700">
                    {t("Mark as Featured")}
                  </span>
                  <input
                    type="checkbox"
                    checked={bulkUpdateData.isFeatured}
                    onChange={(e) =>
                      setBulkUpdateData({
                        ...bulkUpdateData,
                        isFeatured: e.target.checked,
                      })
                    }
                    className="w-5 h-5 rounded border-gray-300 text-blue-600"
                  />
                </label>
              </div>
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setBulkUpdateModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleBulkUpdate}
                disabled={saving}
                className="flex-1 bg-[#0d223f] text-white px-6 py-3 rounded-lg hover:bg-[#0d223f] font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    <span>{t("Updating...")}</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    {t("Update Products")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {bulkUploadModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            if (uploadingBulkProducts) {
              return;
            }
            setBulkUploadModal(false);
            setBulkUploadFile(null);
            setBulkUploadProgress(null);
            setBulkUploadResult(null);
          }}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-xl"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Upload className="w-6 h-6" />
                {t("Bulk Upload Products")}
              </h3>
              <button
                onClick={() => {
                  if (uploadingBulkProducts) {
                    return;
                  }
                  setBulkUploadModal(false);
                  setBulkUploadFile(null);
                  setBulkUploadProgress(null);
                  setBulkUploadResult(null);
                }}
                className="text-white hover:text-gray-300 disabled:opacity-60"
                disabled={uploadingBulkProducts}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-900">
                    {t("Need the format first?")}
                  </p>
                  <p className="text-sm text-gray-600">
                    {t("Download the sample file and fill your product data.")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadSample}
                  disabled={downloadingTemplate || uploadingBulkProducts}
                  className="px-3 py-2 border border-gray-400 bg-white text-black rounded-lg hover:bg-gray-100 text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                >
                  <Download size={16} />
                  {t("Sample")}
                </button>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Select File")}
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => {
                    const selectedFile = e.target.files?.[0] || null;
                    setBulkUploadFile(selectedFile);
                    setBulkUploadProgress(null);
                    setBulkUploadResult(null);
                  }}
                  className="w-full px-4 py-2 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none"
                />
                {bulkUploadFile && (
                  <p className="text-sm text-gray-600 mt-2">
                    {t("Selected")}: <strong>{bulkUploadFile.name}</strong>
                  </p>
                )}
              </div>

              {bulkUploadProgress && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between text-sm font-semibold text-gray-800 mb-2">
                    <span>
                      {t("Status")}: {bulkUploadProgress.status}
                    </span>
                    {typeof bulkUploadProgress.percentage === "number" && (
                      <span>{Math.round(bulkUploadProgress.percentage)}%</span>
                    )}
                  </div>
                  {typeof bulkUploadProgress.percentage === "number" && (
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 transition-all"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(0, bulkUploadProgress.percentage),
                          )}%`,
                        }}
                      />
                    </div>
                  )}
                  {typeof bulkUploadProgress.processed === "number" &&
                    typeof bulkUploadProgress.total === "number" && (
                      <p className="text-xs text-gray-600 mt-2">
                        {bulkUploadProgress.processed} /{" "}
                        {bulkUploadProgress.total} {t("processed")}
                      </p>
                    )}
                  {bulkUploadProgress.message && (
                    <p className="text-xs text-gray-600 mt-2">
                      {bulkUploadProgress.message}
                    </p>
                  )}
                  {bulkUploadProgress.error && (
                    <p className="text-xs text-red-600 mt-2">
                      {bulkUploadProgress.error}
                    </p>
                  )}
                </div>
              )}

              {bulkUploadResult && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-green-800 mb-2">
                    {t("Upload summary")}
                  </p>
                  {bulkUploadResult.message && (
                    <p className="text-xs text-green-900 mb-3">
                      {bulkUploadResult.message}
                    </p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    <div className="bg-white border border-green-200 rounded p-2">
                      <p className="text-[11px] text-gray-500">{t("Total")}</p>
                      <p className="text-sm font-bold text-green-800">
                        {bulkUploadResult.results?.total ?? 0}
                      </p>
                    </div>
                    <div className="bg-white border border-green-200 rounded p-2">
                      <p className="text-[11px] text-gray-500">
                        {t("Processed")}
                      </p>
                      <p className="text-sm font-bold text-green-800">
                        {bulkUploadResult.results?.processed ?? 0}
                      </p>
                    </div>
                    <div className="bg-white border border-green-200 rounded p-2">
                      <p className="text-[11px] text-gray-500">
                        {t("Success")}
                      </p>
                      <p className="text-sm font-bold text-green-700">
                        {bulkUploadResult.results?.success?.length ?? 0}
                      </p>
                    </div>
                    <div className="bg-white border border-red-200 rounded p-2">
                      <p className="text-[11px] text-gray-500">{t("Failed")}</p>
                      <p className="text-sm font-bold text-red-700">
                        {bulkUploadResult.results?.errors?.length ?? 0}
                      </p>
                    </div>
                    <div className="bg-white border border-yellow-200 rounded p-2">
                      <p className="text-[11px] text-gray-500">
                        {t("Skipped")}
                      </p>
                      <p className="text-sm font-bold text-yellow-700">
                        {bulkUploadResult.results?.skipped?.length ?? 0}
                      </p>
                    </div>
                  </div>

                  {typeof bulkUploadResult.results?.currentRow === "number" && (
                    <p className="text-xs text-gray-700 mt-3">
                      {`Current row: ${bulkUploadResult.results.currentRow}`}
                    </p>
                  )}

                  {(bulkUploadResult.results?.errors?.length || 0) > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-red-700 mb-1">
                        {t("Failed rows")}
                      </p>
                      <div className="space-y-1 max-h-28 overflow-y-auto">
                        {(bulkUploadResult.results?.errors || [])
                          .slice(0, 8)
                          .map((item, index) => (
                            <p
                              key={`error-${item.row || index}-${item.sku || ""}`}
                              className="text-xs text-red-700 bg-red-100 rounded px-2 py-1"
                            >
                              {`Row ${item.row ?? "-"} | SKU: ${item.sku || "-"} | ${item.error || item.message || "Failed"}`}
                            </p>
                          ))}
                      </div>
                    </div>
                  )}

                  {(bulkUploadResult.results?.skipped?.length || 0) > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-yellow-700 mb-1">
                        {t("Skipped rows")}
                      </p>
                      <div className="space-y-1 max-h-24 overflow-y-auto">
                        {(bulkUploadResult.results?.skipped || [])
                          .slice(0, 8)
                          .map((item, index) => (
                            <p
                              key={`skipped-${item.row || index}-${item.sku || ""}`}
                              className="text-xs text-yellow-800 bg-yellow-100 rounded px-2 py-1"
                            >
                              {`Row ${item.row ?? "-"} | SKU: ${item.sku || "-"} | ${item.reason || item.message || "Skipped"}`}
                            </p>
                          ))}
                      </div>
                    </div>
                  )}

                  {(bulkUploadResult.results?.success?.length || 0) > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-green-700 mb-1">
                        {t("Created/Updated rows")}
                      </p>
                      <div className="space-y-1 max-h-24 overflow-y-auto">
                        {(bulkUploadResult.results?.success || [])
                          .slice(0, 8)
                          .map((item, index) => (
                            <p
                              key={`success-${item.row || index}-${item.sku || ""}`}
                              className="text-xs text-green-800 bg-green-100 rounded px-2 py-1"
                            >
                              {`Row ${item.row ?? "-"} | SKU: ${item.sku || "-"} | ${item.action || "created/updated"}`}
                            </p>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => {
                  if (uploadingBulkProducts) {
                    return;
                  }
                  setBulkUploadModal(false);
                  setBulkUploadFile(null);
                  setBulkUploadProgress(null);
                  setBulkUploadResult(null);
                }}
                disabled={uploadingBulkProducts}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold disabled:opacity-50"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleUploadWithSSE}
                disabled={uploadingBulkProducts || !bulkUploadFile}
                className="flex-1 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {uploadingBulkProducts ? (
                  <>
                    <div
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    <span>{t("Uploading...")}</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    {t("Start Upload")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {statsModal && productStats && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setStatsModal(false)}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-black px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <BarChart3 className="w-6 h-6" />
                {t("Product Statistics")}
              </h3>
              <button
                onClick={() => setStatsModal(false)}
                className="text-white hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="flex flex-row items-center justify-between bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl gap-1">
                  <Package2 className="w-8 h-8 text-blue-600 mb-2" />
                  <div className="text-sm text-blue-700 font-semibold">
                    {t("Total")}
                  </div>
                  <div className="text-3xl font-bold text-blue-900">
                    {productStats.total}
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl gap-1">
                  <CheckCircle className="w-8 h-8 text-green-600 mb-2" />
                  <div className="text-sm text-green-700 font-semibold">
                    {t("Active")}
                  </div>
                  <div className="text-3xl font-bold text-green-900">
                    {productStats.active}
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl gap-1">
                  <Star className="w-8 h-8 text-green-600 mb-2" />
                  <div className="text-sm text-green-700 font-semibold">
                    {t("Featured")}
                  </div>
                  <div className="text-3xl font-bold text-green-900">
                    {productStats.featuredProducts || 0}
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl gap-1">
                  <AlertTriangle className="w-8 h-8 text-red-600 mb-2" />
                  <div className="text-sm text-red-700 font-semibold">
                    {t("Low Stock")}
                  </div>
                  <div className="text-3xl font-bold text-red-900">
                    {productStats.lowStock}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                  <h4 className="font-bold text-purple-900 mb-4 flex items-center gap-2">
                    {currency.symbol}
                    {t("Value Statistics")}
                  </h4>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-semibold text-gray-700">
                        {t("Total Count")}
                      </span>
                      <span className="px-3  bg-purple-100 text-purple-800 rounded-full font-bold">
                        {Number(productStats?.total)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-semibold text-gray-700">
                        {t("Active products count")}
                      </span>
                      <span className="px-3  bg-purple-100 text-purple-800 rounded-full font-bold">
                        {Number(productStats?.active)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-semibold text-gray-700">
                        {t("Inactive products count")}
                      </span>
                      <span className="px-3  bg-purple-100 text-purple-800 rounded-full font-bold">
                        {Number(productStats?.inactive)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-semibold text-gray-700">
                        {t("OutOfStock products count")}
                      </span>
                      <span className="px-3  bg-purple-100 text-purple-800 rounded-full font-bold">
                        {Number(productStats?.outOfStock)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-semibold text-gray-700">
                        {t("LowStock products count")}
                      </span>
                      <span className="px-3  bg-purple-100 text-purple-800 rounded-full font-bold">
                        {Number(productStats?.lowStock)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-semibold text-gray-700">
                        {t("Total Value")}
                      </span>
                      <span className="px-3  bg-purple-100 text-purple-800 rounded-full font-bold">
                        {/* { JSON.stringify(productStats) }
                        {"total":3,"active":3,"inactive":0,"outOfStock":0,"lowStock":0,"totalValue":43.94} */}
                        <Price amount={Number(productStats?.totalValue)} />
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="font-semibold text-gray-700">
                        {t("Average Price")}
                      </span>
                      <span className="px-3  bg-purple-100 text-purple-800 rounded-full font-bold">
                        <Price
                          amount={Number(
                            productStats?.averagePrice ||
                              productStats?.totalValue /
                                (productStats.total || 1),
                          )}
                        />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6">
              <button
                onClick={() => setStatsModal(false)}
                className="w-full bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold"
              >
                {t("Close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Favorite Check Modal */}
      {favoriteCheckModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-purple-600 to-purple-800 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Star className="w-6 h-6" />
                {t("Check Favorite Status")}
              </h3>
              <button
                onClick={() => setFavoriteCheckModal(false)}
                className="text-white hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Enter Product IDs (comma-separated)")}
                </label>
                <textarea
                  value={favoriteCheckProducts.join(",")}
                  onChange={(e) =>
                    setFavoriteCheckProducts(
                      e.target.value
                        .split(",")
                        .map((id) => id.trim())
                        .filter(Boolean),
                    )
                  }
                  placeholder="product-id-1, product-id-2, product-id-3"
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-purple-600 focus:outline-none resize-none"
                />
              </div>

              {Object.keys(favoriteResults)?.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-bold text-purple-900 mb-3">
                    {t("Results")}:
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(favoriteResults).map(([id, isFavorite]) => (
                      <div
                        key={id}
                        className="flex items-center justify-between p-2 bg-white rounded"
                      >
                        <span className="text-sm text-gray-700">{id}</span>
                        {isFavorite ? (
                          <span className="flex items-center gap-1 text-yellow-600 font-semibold">
                            <Star size={16} fill="currentColor" />
                            Favorite
                          </span>
                        ) : (
                          <span className="text-gray-500 font-semibold">
                            Not Favorite
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setFavoriteCheckModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold"
              >
                {t("Close")}
              </button>
              <button
                onClick={checkFavoriteStatus}
                disabled={saving || favoriteCheckProducts?.length === 0}
                className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    <span>{t("Checking...")}</span>
                  </>
                ) : (
                  <>
                    <Star className="w-5 h-5" />
                    {t("Check Status")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <PrintSlipModal
        product={slipProduct}
        onClose={() => setSlipProduct(null)}
        onPrinted={setPdfUrl}
      />
      {pdfUrl && (
        <iframe
          id="print-frame"
          src={pdfUrl}
          style={{ display: "none" }}
          title="Print Slip"
        />
      )}

      {/* Pagination indicators */}
      {/* ✅ UNIFIED PAGINATION - Works with everything! */}
      {finalTotalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={finalTotalPages}
          totalItems={finalTotalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setItemsPerPage(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          showPageSizeSelector={true}
          isLoading={loading}
          compact={true}
        />
      )}
    </div>
  );
};

export default ProductsManagementPage;
